/**
 * Token Storage Strategy: JavaScript-Accessible Cookies
 *
 * This module stores authentication tokens in cookies that are accessible to JavaScript.
 *
 * Security Considerations:
 * 1. XSS Vulnerability: Tokens ARE accessible to JavaScript via getAccessToken()/getRefreshToken().
 *    If an attacker injects malicious code (XSS attack), they can read and exfiltrate tokens.
 *    This application relies on CSP headers and input sanitization to prevent XSS.
 * 2. Server-Side Route Protection: Next.js middleware can read cookies server-side, enabling
 *    route guards at the middleware layer (before pages render), preventing unauthorized content leaks.
 * 3. CSRF Protection: Cookies use SameSite=Strict, ensuring cookies are only sent with
 *    same-site requests, mitigating Cross-Site Request Forgery attacks.
 * 4. Automatic Expiration: The Max-Age attribute ensures tokens expire automatically,
 *    and Secure flag (in production) ensures transmission only over HTTPS.
 *
 * Why not HttpOnly cookies?
 * HttpOnly cookies (which cannot be read by JavaScript) can only be set by the server via
 * Set-Cookie headers. This implementation uses document.cookie for simplicity, which cannot
 * set HttpOnly flags. To achieve true HttpOnly protection, the backend would need to return
 * Set-Cookie headers instead of JSON tokens, and this client-side code would be removed.
 */

const ACCESS_TOKEN_KEY = 'mtag_access_token'
const REFRESH_TOKEN_KEY = 'mtag_refresh_token'

export type JwtPayload = {
  exp?: number
  iat?: number
  sub?: string
  [key: string]: unknown
}

export type AuthTokens = {
  access_token: string
  refresh_token?: string
  expires_in?: number
  token_type?: string
}

export function decodeJwt(token: string): JwtPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) {
      return null
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    )
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(jsonPayload) as JwtPayload
  } catch {
    return null
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token)
  if (!payload?.exp) {
    // No exp claim — trust the cookie Max-Age set by setTokens()
    return false
  }
  return Date.now() / 1000 >= payload.exp
}

export function getSecondsUntilExpiry(token: string): number {
  const payload = decodeJwt(token)
  if (!payload?.exp) {
    // No exp claim — fall back to the default access token lifetime (15 min)
    return 900
  }
  return payload.exp - Date.now() / 1000
}

function buildCookieString(
  name: string,
  value: string,
  maxAge: number,
): string {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  return `${name}=${encodeURIComponent(
    value,
  )}; Path=/; SameSite=Strict; Max-Age=${maxAge}${secure}`
}

export function setTokens(tokens: AuthTokens): void {
  const accessMaxAge = tokens.expires_in ?? 900
  document.cookie = buildCookieString(
    ACCESS_TOKEN_KEY,
    tokens.access_token,
    accessMaxAge,
  )
  // Refresh token valid for 30 days (only store if provided)
  if (tokens.refresh_token) {
    document.cookie = buildCookieString(
      REFRESH_TOKEN_KEY,
      tokens.refresh_token,
      60 * 60 * 24 * 30,
    )
  }
}

export function getAccessToken(): string | null {
  return readCookie(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return readCookie(REFRESH_TOKEN_KEY)
}

export function clearTokens(): void {
  document.cookie = `${ACCESS_TOKEN_KEY}=; Path=/; Max-Age=0`
  document.cookie = `${REFRESH_TOKEN_KEY}=; Path=/; Max-Age=0`
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null
  }
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
  if (!match) {
    return null
  }
  const value = match.split('=').slice(1).join('=')
  return value ? decodeURIComponent(value) : null
}
